import type { LucideIcon } from 'lucide-react';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { cn } from '@shared/lib/cn';

interface StatTileProps {
  label: string;
  /** Pre-formatted, so the tile does not need to know about money vs counts. */
  value: string;
  icon: LucideIcon;
  /** Short context line, e.g. "of 43 live". */
  hint?: string;
  /** Period-over-period change as a ratio; null when there is no baseline. */
  delta?: number | null;
  /** Renders the tile as the page's headline figure. */
  accent?: boolean;
  /**
   * Marks a figure that is a problem rather than an achievement. Never the only
   * signal — the label always says what the number is.
   */
  tone?: 'default' | 'warning';
  className?: string;
}

/**
 * A headline number. Per the form heuristic, a single current value is a stat
 * tile — not a one-bar bar chart. The figure carries the emphasis; the icon is
 * decoration and is hidden from assistive tech.
 */
export function StatTile({
  label,
  value,
  icon: Icon,
  hint,
  delta,
  accent = false,
  tone = 'default',
  className,
}: StatTileProps) {
  const isWarning = tone === 'warning' && !accent;

  return (
    <div
      className={cn(
        'rounded-xl border px-3.5 py-3',
        accent
          ? 'border-transparent bg-gradient-to-br from-periscope-700 to-periscope-500 text-white shadow-sm'
          : isWarning
            ? 'border-amber-200 bg-amber-50/70'
            : 'border-slate-200 bg-white',
        className,
      )}
    >
      <div
        className={cn(
          'flex items-center gap-1.5',
          accent ? 'text-white/70' : isWarning ? 'text-amber-700' : 'text-slate-400',
        )}
      >
        <Icon aria-hidden className="h-3.5 w-3.5 shrink-0" />
        <p className="truncate text-[11px] font-medium uppercase tracking-wide">{label}</p>
      </div>

      <div className="mt-1 flex flex-wrap items-baseline gap-1.5">
        <p
          // Proportional figures, not tabular: equal-width digits make a
          // standalone number look loose at display sizes. Tabular belongs
          // where numbers stack in a column, as in the tables.
          className={cn(
            'text-2xl font-semibold tracking-tight',
            accent ? 'text-white' : isWarning ? 'text-amber-900' : 'text-slate-900',
          )}
        >
          {value}
        </p>

        {delta !== undefined && delta !== null && <DeltaPill delta={delta} accent={accent} />}
      </div>

      {hint && (
        <p
          className={cn(
            'mt-0.5 truncate text-[11px]',
            accent ? 'text-white/70' : isWarning ? 'text-amber-700' : 'text-slate-400',
          )}
        >
          {hint}
        </p>
      )}
    </div>
  );
}

function DeltaPill({ delta, accent }: { delta: number; accent: boolean }) {
  const isUp = delta >= 0;
  const Icon = isUp ? ArrowUpRight : ArrowDownRight;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs font-medium',
        accent
          ? 'bg-white/20 text-white'
          : isUp
            ? 'bg-periscope-100 text-periscope-800'
            : 'bg-amber-100 text-amber-800',
      )}
    >
      <Icon aria-hidden className="h-3 w-3" />
      {/* Direction is carried by the arrow and the sign, never by colour alone. */}
      {isUp ? '+' : ''}
      {formatDelta(delta)}
    </span>
  );
}

/** Caps runaway percentages: "+679%" is honest, "+67900%" is just noise. */
function formatDelta(delta: number): string {
  const percent = delta * 100;

  if (Math.abs(percent) >= 1000) {
    return `${Math.round(percent / 100) / 10}k%`;
  }

  return `${Math.round(percent)}%`;
}
